import { isEndUserMessage, isMeetingScheduleMessage } from 'modules/message/utils'
import { useEffect, useRef,useState } from 'react'
import { ConversationId } from 'types/conversation'
import { Message } from 'types/message'

const ONE_MINUTE_IN_MS = 1000 * 60
const DELAY_TIME_IN_MS = 600

const useTimedMessages = (messages: Message[], conversationId: ConversationId) => {
  const [timedMessages, setTimedMessages] = useState<Message[]>(() => messages)
  const conversationIdRef = useRef(conversationId)
  const timeoutRef = useRef<number>(null)

  useEffect(() => {
    if (!conversationId) {
      if (timedMessages.length > 0) {
        setTimedMessages([])
      }
      return
    }

    if (messages === timedMessages) {
      return
    }

    const numTimedMessages = timedMessages.length

    if (messages.length === 0 && numTimedMessages > 0) {
      setTimedMessages([])
      return
    }

    if (conversationIdRef.current !== conversationId || numTimedMessages === messages.length) {
      // The convo has changed or we may have a message update
      conversationIdRef.current = conversationId
      setTimedMessages(messages)
      return
    }

    const pushMessage = () => setTimedMessages(messages.slice(0, numTimedMessages + 1))

    const lastTimedMessage : Message = messages[numTimedMessages] || messages[0]
    const isOldMessage = (Date.now() - lastTimedMessage.createdAt) > ONE_MINUTE_IN_MS
    if (isOldMessage || isEndUserMessage(lastTimedMessage) || isMeetingScheduleMessage(lastTimedMessage)) {
      // These messages should be pushed immediately
      pushMessage()
      return
    }

    // Delay agent messages
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null
      pushMessage()
    }, DELAY_TIME_IN_MS)

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }

  }, [messages, timedMessages, conversationId])

  return timedMessages
}

export default useTimedMessages
